import {
  Box,
  Button,
  Card,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { TableEmptyRows, TableHeadCustom, TableNoData } from 'src/components/table';
import useTable, { emptyRows, getComparator } from 'src/hooks/useTable';
import { applySortFilter } from '../../pages/FhirOrganizationList';
import FhirOrganizationTableToolbar from '../common/FhirOrganizationTableToolbar';
import FhirOrganizationTableRow from '../common/FhirOrganizationTableRow';
import ChildModal from '../common/FhirOrganizationListModal';
import { WrappedOrganization } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { translateTableHead } from 'src/careflow/helpers/common';
import { TABLE_HEAD_ORGANIZATION } from 'src/careflow/common/table-head';

type Props = {
  organization: {
    main: WrappedOrganization | null;
    partOf: WrappedOrganization | null;
    childs: WrappedOrganization[] | null;
  };
  translate: any;
  update: Function;
};

export function ChildList({ organization, translate, update }: Props) {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const [filterName, setFilterName] = useState('');
  const [openChildModal, setOpenChildModal] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleRemoveChild = async (row: WrappedOrganization) => {
    try {
      await update({
        ...row,
        partOf: null,
      });

      enqueueSnackbar(
        `${row?.name} ${translate('dialogs.childRemoved')} ${organization?.main?.name}`
      );
    } catch (error) {
      enqueueSnackbar(translate('dialogs.error'), { variant: 'error' });
    }
  };

  const handleAddChild = async (row: any) => {
    try {
      const orgSelectedMapn = row?.map((org) => ({
        ...org,
        ...(!org?.active && { active: true }),
        partOf: {
          reference: `Organization/${organization?.main?.id}`,
        },
      }));

      await update(orgSelectedMapn);

      const count = row?.length ?? 0;

      const message =
        count === 1 ? translate('dialogs.childAdded') : translate('dialogs.childsAdded');

      enqueueSnackbar(message);
    } catch (error) {
      enqueueSnackbar(translate('dialogs.error'), { variant: 'error' });
    }
  };

  const dataFiltered = applySortFilter({
    tableData: organization?.childs ?? [],
    comparator: getComparator(order, orderBy),
    name: filterName,
    active: true,
    type: [],
  });

  const getIdsAllOrg = useMemo<string[]>(
    () =>
      [
        organization?.main?.id,
        organization?.partOf?.id,
        ...(organization?.childs?.map((child) => child.id) ?? []),
      ].filter((id): id is string => Boolean(id)),
    [organization]
  );

  const isNotFound = !dataFiltered.length;

  const isAllowedAddChildOrg = checkAclValidation({
    acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CHILD_ORGANIZATIONS.ADD],
  });

  const isAllowedEditChildOrg = checkAclValidation({
    acls: [crsAcls.ADMIN.FHIR_ORGANIZATIONS.CHILD_ORGANIZATIONS.EDIT],
  });

  const denseHeight = dense ? 52 : 72;

  return (
    <Card sx={{ width: '100%', height: '100%', paddingTop: 3 }}>
      <FhirOrganizationTableToolbar
        searchTextFieldProps={{
          placeholder: translate('list.filter.searchFieldPlaceholder'),
          onChange: (event) => {
            setFilterName(event.target.value);
            setPage(0);
          },
        }}
        containerSx={{ marginBottom: 3, paddingX: 2.5 }}
        childrenFilter={
          <Button
            disabled={!isAllowedAddChildOrg}
            onClick={() => setOpenChildModal(true)}
            variant="contained"
            startIcon={<Iconify icon={'eva:plus-fill'} />}
          >
            {translate('details.child.addButton')}
          </Button>
        }
      />

      <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
        <Table size={dense ? 'small' : 'medium'}>
          <TableHeadCustom
            order={order}
            orderBy={orderBy}
            headLabel={translateTableHead(TABLE_HEAD_ORGANIZATION, translate, 'translations')}
            rowCount={organization?.childs?.length}
            onSort={onSort}
          />

          <TableBody>
            {dataFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
              <FhirOrganizationTableRow
                key={row.id}
                row={row}
                onDeleteRow={null}
                onEditRow={() => {}}
                menuItems={
                  <>
                    {isAllowedEditChildOrg && (
                      <MenuItem onClick={() => handleRemoveChild(row)} sx={{ color: 'error.main' }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Iconify icon="eva:trash-2-outline" />
                          <Typography variant="body2">{translate('list.delete')}</Typography>
                        </Stack>
                      </MenuItem>
                    )}
                  </>
                }
                translate={translate}
              />
            ))}

            <TableEmptyRows
              height={denseHeight}
              emptyRows={emptyRows(page, rowsPerPage, Number(organization?.childs?.length))}
            />

            <TableNoData isNotFound={isNotFound} />
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={dataFiltered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />

        <FormControlLabel
          control={<Switch checked={dense} onChange={onChangeDense} />}
          label="Dense"
          sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
        />
      </Box>
      <ChildModal
        open={openChildModal}
        translate={translate}
        handleAction={handleAddChild}
        onClose={() => setOpenChildModal(false)}
        filterIdsOrg={getIdsAllOrg}
      />
    </Card>
  );
}
